import { ExcludeDateInterval } from 'components/NewForm';
import { DATE_TIME_PICKER_TIME_INTERVAL, ONE_DAY, ONE_MINUTE } from 'lib/constants';
import { MonthAbbreviation } from 'lib/enum/month-abbreviation.enum';
import { MonthName } from 'lib/types/month-name';
import pluralize from 'pluralize';
import dayjs, { Dayjs, QUnitType } from 'dayjs';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
dayjs.extend(duration);
dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);

export const getFormattedLocalDate = (value?: Date | string | number | null, separator?: '/' | '-' | '.'): string => {
    if (value) {
        return dayjs(value).format(`DD${separator || '-'}MM${separator || '-'}YYYY`);
    }
    return '-';
};

export const getFormattedLocalTime = (value?: Date | string | null): string => {
    if (value) {
        return dayjs(value).format('HH:mm');
    }
    return '-';
};

export const getFormattedLocalDateTime = (value?: Date | string | null, dateSeparator?: '/' | '-' | '.'): string => {
    if (value) {
        return `${getFormattedLocalDate(value, dateSeparator)}, ${getFormattedLocalTime(value)}`;
    }
    return '-';
};

export const getDurationTimeString = (duration: duration.Duration): string => {
    const years = Math.floor(duration.asYears());
    if (years) {
        return `${years} ${pluralize('year', years)}`;
    }
    const months = Math.floor(duration.asMonths());
    if (months) {
        return `${months} ${pluralize('month', months)}`;
    }
    const days = Math.floor(duration.asDays());
    if (days) {
        return `${days} ${pluralize('day', days)}`;
    }
    const hours = Math.floor(duration.asHours());
    if (hours) {
        return `${hours} ${pluralize('hour', hours)}`;
    }
    const minutes = Math.floor(duration.asMinutes());
    return `${minutes} ${pluralize('minute', minutes)}`;
};
export const getDisplayDateFormatDMY = (date: Date): string => {
    const monthName = dayjs(date).format('MMMM') as MonthName;
    const monthAbbreviation = MonthAbbreviation[monthName];
    return `${dayjs(date).format('DD')} ${monthAbbreviation} ${dayjs(date).format('YYYY')}`;
};

export const getDuration = (startDate: string | null, endDate: string | null): duration.Duration =>
    dayjs.duration(dayjs(endDate).diff(startDate));

export const getDurationString = (startDate: string | null, endDate: string | null): string => {
    const duration = getDuration(startDate, endDate);

    if (endDate === null || duration.asMilliseconds() <= 0) {
        return 'Ongoing';
    }
    return getDurationTimeString(duration);
};

export const getLastTime = (date: string | null): duration.Duration =>
    getDuration(date, dayjs(new Date()).toISOString());

export const getLastTimeString = (date: string | null): string => {
    const duration = getLastTime(date);
    if (date === null || duration.asMilliseconds() <= 0) {
        return 'Now';
    }
    return `${getDurationTimeString(duration)} ago`;
};

export const getExcludedTimes = (
    selectedDate: Date | null | undefined,
    excludeDateIntervals: ExcludeDateInterval[] | undefined,
    minDate: Date | null | undefined,
    maxDate: Date | null | undefined,
): Date[] => {
    let excludedTimes: Date[] = [];
    if (selectedDate && excludeDateIntervals) {
        const selectedMoment = dayjs(selectedDate);
        const interval = excludeDateIntervals.find(
            ({ start, end }) => dayjs(start).isSame(selectedMoment, 'day') || dayjs(end).isSame(selectedMoment, 'day'),
        );
        if (minDate && dayjs(minDate).isSame(selectedMoment, 'day')) {
            excludedTimes = [
                ...excludedTimes,
                ...getTimesByInterval(
                    dayjs(minDate).startOf('day').subtract(ONE_MINUTE, 'minute').toDate(),
                    minDate,
                    DATE_TIME_PICKER_TIME_INTERVAL,
                ),
            ];
        }
        if (dayjs(interval?.start ?? null).isSame(selectedMoment, 'day')) {
            excludedTimes = [
                ...excludedTimes,
                ...getTimesByInterval(
                    interval!.start,
                    dayjs(interval!.start).add(ONE_DAY, 'days').startOf('day').subtract(ONE_MINUTE, 'minute').toDate(),
                    DATE_TIME_PICKER_TIME_INTERVAL,
                ),
            ];
        }
        if (dayjs(interval?.end ?? null).isSame(selectedMoment, 'day')) {
            excludedTimes = [
                ...excludedTimes,
                ...getTimesByInterval(
                    dayjs(interval!.end).startOf('day').subtract(ONE_MINUTE, 'minute').toDate(),
                    dayjs(interval!.end).subtract(ONE_MINUTE, 'minute').toDate(),
                    DATE_TIME_PICKER_TIME_INTERVAL,
                ),
            ];
        }
        if (maxDate && dayjs(maxDate).isSame(selectedMoment, 'day')) {
            excludedTimes = [
                ...excludedTimes,
                ...getTimesByInterval(
                    maxDate,
                    dayjs(maxDate).add(ONE_DAY, 'days').startOf('day').subtract(ONE_MINUTE, 'minute').toDate(),
                    DATE_TIME_PICKER_TIME_INTERVAL,
                ),
            ];
        }
    }
    return excludedTimes;
};

const getTimesByInterval = (from: Date, to: Date, interval: number): Date[] => {
    const dates = [];
    const minutes = from.getMinutes();
    const difference = interval - (minutes % interval);
    let currentDate = dayjs(from).add(difference, 'minutes').startOf('minute');
    while (currentDate.isSameOrBefore(dayjs(to))) {
        dates.push(currentDate.toDate());
        currentDate = currentDate.add(interval, 'minutes');
    }
    return dates;
};

export const isInPast = (date: Date | Dayjs | string, granularity?: QUnitType): boolean => {
    return dayjs(date).isBefore(dayjs(), granularity);
};

export const areEqual = (
    date1: Date | Dayjs | string,
    date2: Date | Dayjs | string,
    granularity?: QUnitType,
): boolean => {
    return dayjs(date1).isSame(date2, granularity);
};
